import { devLog } from './devLog';
/**
 * Unified Diff Parser Utility
 * 
 * Standalone utility for parsing and applying unified diff format
 * Does NOT require Monaco editor instance - can be used for background processing
 * 
 * Enhanced with:
 * - Fuzzy matching for context lines
 * - Multi-file diff support
 * - Smart hunk positioning
 * - Better error recovery
 * - Adaptive line matching with Levenshtein distance
 * - Content-aware parsing with normalization
 * - Pre-validation and partial application
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/** Minimum similarity score required for fuzzy matching (0-1 scale) */
const SIMILARITY_THRESHOLD = 0.7;

/** Minimum number of exact matches required for hunk positioning */
const MIN_EXACT_MATCHES = 1;

/** Default search window size for finding hunk positions (lines before/after) */
const DEFAULT_SEARCH_WINDOW = 50;

/** Default search window for hunk application (larger for more flexibility) */
const DEFAULT_HUNK_SEARCH_WINDOW = 500;

/** Default maximum number of allowed mismatches before rejecting diff */
const DEFAULT_MAX_MISMATCHES = 50;

/** Similarity bonus multiplier for exact matches (up to 10% boost) */
const EXACT_MATCH_BONUS = 0.1;

/** Minimum confidence threshold for applying a hunk (15% allows more flexible fuzzy matching) */
const MIN_CONFIDENCE_THRESHOLD = 0.15;

/** Confidence threshold for warnings (below this, warn user) */
const LOW_CONFIDENCE_WARNING = 0.8;

/** Confidence threshold for rejecting repositioned hunks (15% to match MIN_CONFIDENCE_THRESHOLD) */
const MIN_REPOSITION_CONFIDENCE = 0.15;

/** Default similarity threshold for fuzzy matching context lines */
const DEFAULT_SIMILARITY_THRESHOLD = 0.5;

/** Similarity score for normalized whitespace matches */
const NORMALIZED_MATCH_SCORE = 0.95;

/** Pattern for detecting inline UI structured content blocks */
const STRUCTURED_BLOCK_PATTERN = /\[STRUCTURED:[a-z-]+\]\s*[\s\S]*?\s*\[\/STRUCTURED*\]/gi;

/** Pattern for detecting incomplete structured blocks */
const INCOMPLETE_STRUCTURED_PATTERN = /\[STRUCTURED:[a-z-]+\][\s\S]*$/i;

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Remove inline UI structured content blocks from diff content
 * These are JSON blocks wrapped in [STRUCTURED:type]...[/STRUCTURED] markers
 * that should not appear in code diffs
 * @param {string} text - Text that may contain structured blocks
 * @returns {string} Text with structured blocks removed
 */
function stripStructuredBlocks(text) {
  if (!text) return text;
  
  let cleanText = text.replace(STRUCTURED_BLOCK_PATTERN, '');
  cleanText = cleanText.replace(INCOMPLETE_STRUCTURED_PATTERN, '');
  
  return cleanText.trim();
}

/**
 * Calculate similarity between two strings (0-1 scale)
 * Uses normalization and Levenshtein distance for fuzzy matching
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Similarity score (0-1)
 */
function calculateSimilarity(str1, str2) {
  if (str1 === str2) return 1.0;
  
  // Normalize whitespace for comparison
  const norm1 = str1.trim().replace(/\s+/g, ' ');
  const norm2 = str2.trim().replace(/\s+/g, ' ');
  
  if (norm1 === norm2) return NORMALIZED_MATCH_SCORE;
  
  // Calculate Levenshtein distance for fuzzy matching
  const maxLen = Math.max(str1.length, str2.length);
  if (maxLen === 0) return 1.0;
  
  const distance = levenshteinDistance(norm1, norm2);
  return 1.0 - (distance / maxLen);
}

/**
 * Calculate Levenshtein distance between two strings
 * Used for fuzzy matching to handle minor text differences
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} - Edit distance
 */
function levenshteinDistance(str1, str2) {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix = Array(len2 + 1).fill(null).map(() => Array(len1 + 1).fill(0));
  
  for (let i = 0; i <= len1; i++) matrix[0][i] = i;
  for (let j = 0; j <= len2; j++) matrix[j][0] = j;
  
  for (let j = 1; j <= len2; j++) {
    for (let i = 1; i <= len1; i++) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,     // deletion
        matrix[j - 1][i] + 1,     // insertion
        matrix[j - 1][i - 1] + indicator // substitution
      );
    }
  }
  
  return matrix[len2][len1];
}

/**
 * Find best matching position for a hunk in the file
 * Uses context-based anchoring with fuzzy matching and adaptive positioning
 * @param {array} lines - File lines
 * @param {array} hunkLines - All hunk lines (including context and changes)
 * @param {number} suggestedLine - Suggested line number
 * @param {number} searchWindow - How many lines to search around suggested position
 * @returns {object} - {position: number, confidence: number, exactMatches: number}
 */
function findBestHunkPosition(lines, hunkLines, suggestedLine, searchWindow = DEFAULT_SEARCH_WINDOW) {
  
  // Handle empty file - return position 0 with high confidence
  if (lines.length === 0) {
    return {position: 0, confidence: 1.0, exactMatches: 0};
  }
  
  // Extract context and deletion lines for matching
  const matchLines = hunkLines
    .filter(line => line[0] === ' ' || line[0] === '-')
    .map(line => line.substring(1));
  
  if (matchLines.length === 0) {
    return {position: suggestedLine - 1, confidence: 0.5, exactMatches: 0};
  }
  
  // Try exact position first
  const exactMatch = checkHunkMatch(lines, matchLines, suggestedLine - 1);
  if (exactMatch.confidence > 0.5 && exactMatch.exactMatches >= MIN_EXACT_MATCHES) {
    return {
      position: suggestedLine - 1,
      confidence: exactMatch.confidence,
      exactMatches: exactMatch.exactMatches
    };
  }
  
  // Search around suggested position
  const startSearch = Math.max(0, suggestedLine - searchWindow);
  const endSearch = Math.min(lines.length, suggestedLine + searchWindow);
  
  let bestMatch = {
    position: suggestedLine - 1,
    confidence: exactMatch.confidence,
    exactMatches: exactMatch.exactMatches
  };
  
  for (let pos = startSearch; pos < endSearch; pos++) {
    const match = checkHunkMatch(lines, matchLines, pos);
    
    // Prefer matches with more exact matches, then higher confidence
    const isBetter = match.exactMatches > bestMatch.exactMatches ||
                     (match.exactMatches === bestMatch.exactMatches && match.confidence > bestMatch.confidence);
    
    if (isBetter) {
      bestMatch = {position: pos, confidence: match.confidence, exactMatches: match.exactMatches};
    }
    
    // If we find a very good match with exact matches, stop searching
    if (match.confidence > 0.95 && match.exactMatches >= MIN_EXACT_MATCHES) break;
  }
  
  // Only use fuzzy match if confidence is above threshold AND we have some exact matches
  if (bestMatch.confidence >= SIMILARITY_THRESHOLD && bestMatch.exactMatches >= MIN_EXACT_MATCHES) {
    return bestMatch;
  }
  
  // Fall back to suggested position if fuzzy match is not good enough
  return {
    position: suggestedLine - 1,
    confidence: exactMatch.confidence,
    exactMatches: exactMatch.exactMatches
  };
}

/**
 * Check how well a hunk matches at a specific position
 * Returns confidence score and exact match count for validation
 * @param {array} lines - File lines
 * @param {array} contextLines - Context lines from diff
 * @param {number} startPos - Starting position to check
 * @returns {object} - {confidence: number, exactMatches: number}
 */
function checkHunkMatch(lines, contextLines, startPos) {
  if (contextLines.length === 0) return {confidence: 0.5, exactMatches: 0};
  
  // Empty file - return low confidence unless we're at position 0
  if (lines.length === 0) {
    return startPos === 0 ? {confidence: 1.0, exactMatches: 0} : {confidence: 0, exactMatches: 0};
  }
  
  let totalSimilarity = 0;
  let comparedLines = 0;
  let exactMatches = 0;
  
  for (let i = 0; i < contextLines.length; i++) {
    const lineIdx = startPos + i;
    if (lineIdx >= lines.length) break;
    
    const similarity = calculateSimilarity(contextLines[i], lines[lineIdx]);
    totalSimilarity += similarity;
    comparedLines++;
    
    if (similarity === 1.0) {
      exactMatches++;
    }
  }
  
  if (comparedLines === 0) return {confidence: 0, exactMatches: 0};
  
  const avgSimilarity = totalSimilarity / comparedLines;
  // Penalize if we couldn't compare all context lines
  const completeness = comparedLines / contextLines.length;
  
  // Bonus for having exact matches
  const exactMatchRatio = exactMatches / contextLines.length;
  const confidenceBoost = exactMatchRatio * EXACT_MATCH_BONUS;
  
  return {
    confidence: Math.min(1.0, avgSimilarity * completeness + confidenceBoost),
    exactMatches
  };
}

// ============================================================================
// DIFF PARSING FUNCTIONS
// ============================================================================

/**
 * Parse unified diff format and extract file operations
 * Handles file creation, deletion, modification, and rename operations
 * Automatically strips inline UI structured content blocks
 * @param {string} diffContent - Raw unified diff content
 * @returns {object} - Parsed diff information
 */
export function parseUnifiedDiff(diffContent) {
  // Strip any inline UI structured blocks from the diff content
  const cleanDiffContent = stripStructuredBlocks(diffContent);
  const diffLines = cleanDiffContent.split('\n');
  let i = 0;
  
  // Find file headers
  while (i < diffLines.length && !diffLines[i].startsWith('---')) {
    i++;
  }
  
  if (i >= diffLines.length) {
    return {
      error: 'Missing --- file header',
      isValid: false
    };
  }
  
  // Parse old file path
  const oldFileHeader = diffLines[i];
  let oldFilePath;
  
  if (oldFileHeader.startsWith('--- a/')) {
    oldFilePath = oldFileHeader.substring(6).trim();
  } else if (oldFileHeader.startsWith('--- /dev/null')) {
    oldFilePath = '/dev/null';
  } else if (oldFileHeader.startsWith('--- ')) {
    oldFilePath = oldFileHeader.substring(4).trim();
  } else {
    return {
      error: `Unrecognized --- format: ${oldFileHeader}`,
      isValid: false
    };
  }
  
  i++; // Move past ---
  
  // Parse new file path
  if (i >= diffLines.length || !diffLines[i].startsWith('+++')) {
    return {
      error: 'Missing +++ file header',
      isValid: false
    };
  }
  
  const newFileHeader = diffLines[i];
  let newFilePath;
  
  if (newFileHeader.startsWith('+++ b/')) {
    newFilePath = newFileHeader.substring(6).trim();
  } else if (newFileHeader.startsWith('+++ /dev/null')) {
    newFilePath = '/dev/null';
  } else if (newFileHeader.startsWith('+++ ')) {
    newFilePath = newFileHeader.substring(4).trim();
  } else {
    return {
      error: `Unrecognized +++ format: ${newFileHeader}`,
      isValid: false
    };
  }
  
  i++; // Move past +++
  
  // Determine operation type
  const isFileCreation = oldFilePath === '/dev/null' && newFilePath !== '/dev/null';
  const isFileDeletion = oldFilePath !== '/dev/null' && newFilePath === '/dev/null';
  const isFileRename = oldFilePath !== newFilePath && !isFileCreation && !isFileDeletion;
  const isFileModification = oldFilePath === newFilePath;
  
  return {
    isValid: true,
    oldFilePath,
    newFilePath,
    targetPath: newFilePath !== '/dev/null' ? newFilePath : oldFilePath,
    operation: isFileCreation ? 'create' : 
               isFileDeletion ? 'delete' : 
               isFileRename ? 'rename' : 
               'modify',
    isFileCreation,
    isFileDeletion,
    isFileRename,
    isFileModification,
    diffLines,
    headerEndIndex: i
  };
}

/**
 * Extract content from a file creation diff
 * Parses hunks and extracts all added lines to reconstruct file content
 * @param {object} parsedDiff - Result from parseUnifiedDiff
 * @returns {string} - Extracted file content
 */
export function extractFileContent(parsedDiff) {
  if (!parsedDiff.isValid || !parsedDiff.isFileCreation) {
    return '';
  }
  
  const {diffLines, headerEndIndex} = parsedDiff;
  const unifiedDiffRegex = /@@\s*-(\d+)(?:,(\d+))?\s*\+(\d+)(?:,(\d+))?\s*@@/;
  
  let contentLines = [];
  let i = headerEndIndex;
  let foundHeader = false;
  
  while (i < diffLines.length) {
    const line = diffLines[i];
    
    // Find @@ header
    if (line.match(unifiedDiffRegex)) {
      foundHeader = true;
      i++;
      continue;
    }
    
    // After header, collect lines with + prefix
    if (foundHeader) {
      if (line.startsWith('+')) {
        contentLines.push(line.substring(1)); // Remove + prefix
      } else if (line.startsWith(' ')) {
        // Context line (shouldn't happen in new file, but include it)
        contentLines.push(line.substring(1));
      } else if (line.startsWith('---') || line.startsWith('+++') || line.match(unifiedDiffRegex)) {
        // End of this file's diff
        break;
      } else if (line.trim() === '' || line.startsWith('\\')) {
        // Empty line or "\ No newline at end of file" - end of diff
        break;
      }
    }
    
    i++;
  }
  
  return contentLines.join('\n');
}

// ============================================================================
// DIFF APPLICATION FUNCTIONS
// ============================================================================

/**
 * Apply unified diff to existing content
 * Main entry point for applying diffs with comprehensive error handling
 * Automatically strips inline UI structured content blocks
 * Detects and handles multi-file diffs by splitting them appropriately
 * Falls back to AI-powered merge using WebLLM if traditional diff fails
 * @param {string} diffContent - Raw unified diff
 * @param {string} currentContent - Current file content
 * @param {object} options - Options for diff application
 * @param {boolean} options.useFuzzyMatching - Enable fuzzy matching (default: true)
 * @param {number} options.similarityThreshold - Similarity threshold for fuzzy matching (default: 0.5)
 * @param {number} options.maxMismatches - Maximum allowed mismatches (default: 50)
 * @param {number} options.searchWindow - Search window for hunk positioning (default: 500)
 * @param {boolean} options.debug - Enable detailed debug logging (default: false)
 * @param {boolean} options.useAIFallback - Enable AI-powered merge fallback (default: true)
 * @param {function} options.onAIProgress - Progress callback for AI merge (optional)
 * @returns {object} - {success: boolean, newContent: string, changes: array, isMultiFile: boolean, files: array, usedAI?: boolean}
 */
export async function applyUnifiedDiff(diffContent, currentContent, options = {}) {
  const {
    useAIFallback = true,
    onAIProgress = null,
    ...otherOptions
  } = options;

  // Strip structured blocks first to clean the diff
  const cleanDiffContent = stripStructuredBlocks(diffContent);
  
  // CRITICAL FIX: Check if this is a multi-file diff before parsing as single file
  // Count how many file headers (---) exist in the diff
  const fileHeaderMatches = cleanDiffContent.match(/^---\s+/gm);
  const fileHeaderCount = fileHeaderMatches ? fileHeaderMatches.length : 0;
  
  // If we have multiple file headers, this is a multi-file diff
  if (fileHeaderCount > 1) {
    // eslint-disable-next-line no-console
    devLog(`🗂️  Detected multi-file diff with ${fileHeaderCount} files - splitting and processing separately`);
    
    // Parse as multi-file diff
    const fileDiffs = parseMultiFileDiff(cleanDiffContent);
    
    if (fileDiffs.length === 0) {
      return {
        success: false,
        error: 'Failed to parse multi-file diff',
        newContent: currentContent,
        changes: [],
        isMultiFile: true,
        files: []
      };
    }
    
    // Return info about all files found in the diff
    // The caller needs to handle each file separately
    const filesInfo = fileDiffs.map(parsed => ({
      targetPath: parsed.targetPath,
      operation: parsed.isFileCreation ? 'create' : 
                 parsed.isFileDeletion ? 'delete' : 
                 parsed.isFileRename ? 'rename' : 
                 'modify',
      diffContent: parsed.diffLines.join('\n'),
      isFileCreation: parsed.isFileCreation,
      isFileDeletion: parsed.isFileDeletion,
      isFileRename: parsed.isFileRename,
      oldFilePath: parsed.oldFilePath,
      newFilePath: parsed.newFilePath
    }));
    
    // eslint-disable-next-line no-console
    devLog(`📋 Multi-file diff contains: ${filesInfo.map(f => `${f.operation} ${f.targetPath}`).join(', ')}`);
    
    return {
      success: true,
      isMultiFile: true,
      files: filesInfo,
      message: `Multi-file diff detected with ${fileHeaderCount} files. Process each file separately.`,
      newContent: currentContent, // No content change - caller must handle each file
      changes: []
    };
  }
  
  // Single file diff - process normally
  const parsedDiff = parseUnifiedDiff(cleanDiffContent);
  
  if (!parsedDiff.isValid) {
    return {
      success: false,
      error: parsedDiff.error,
      newContent: currentContent,
      changes: [],
      isMultiFile: false
    };
  }
  
  // Handle file creation
  if (parsedDiff.isFileCreation) {
    const content = extractFileContent(parsedDiff);
    return {
      success: true,
      operation: 'create',
      newContent: content,
      targetPath: parsedDiff.targetPath,
      changes: [],
      isMultiFile: false
    };
  }
  
  // Handle file deletion
  if (parsedDiff.isFileDeletion) {
    return {
      success: true,
      operation: 'delete',
      targetPath: parsedDiff.targetPath,
      newContent: '',
      changes: [],
      isMultiFile: false
    };
  }
  
  // Handle file modification
  if (parsedDiff.isFileModification) {
    const result = applyDiffToContent(parsedDiff, currentContent, otherOptions);
    
    return {
      success: result.success,
      operation: 'modify',
      targetPath: parsedDiff.targetPath,
      newContent: result.newContent,
      changes: result.changes,
      error: result.error,
      isMultiFile: false
    };
  }
  
  return {
    success: false,
    error: 'Unsupported operation',
    newContent: currentContent,
    changes: [],
    isMultiFile: false
  };
}

/**
 * Apply diff hunks to content (for file modifications)
 * Internal function that processes hunks sequentially with validation
 * @param {object} parsedDiff - Result from parseUnifiedDiff
 * @param {string} currentContent - Current file content
 * @param {object} options - Options for diff application
 * @returns {object} - {success: boolean, newContent: string, changes: array}
 */
function applyDiffToContent(parsedDiff, currentContent, options = {}) {
  const {diffLines, headerEndIndex} = parsedDiff;
  const currentLines = currentContent.split('\n');
  const changes = [];
  
  const unifiedDiffRegex = /@@\s*-(\d+)(?:,(\d+))?\s*\+(\d+)(?:,(\d+))?\s*@@/;
  
  let i = headerEndIndex;
  let resultLines = [...currentLines];
  let hunkCount = 0;
  let cumulativeOffset = 0; // Track total line changes across hunks
  
  // Process each diff hunk
  try {
    while (i < diffLines.length) {
      const line = diffLines[i];
      const match = line.match(unifiedDiffRegex);
      
      if (match) {
        hunkCount++;
        const oldStartLine = parseInt(match[1], 10);
        const oldLineCount = match[2] ? parseInt(match[2], 10) : 1;
        // eslint-disable-next-line no-unused-vars
        const newStartLine = parseInt(match[3], 10);
        // eslint-disable-next-line no-unused-vars
        const newLineCount = match[4] ? parseInt(match[4], 10) : 1;
        
        // Adjust position based on cumulative changes from previous hunks
        const adjustedStartLine = oldStartLine + cumulativeOffset;
        
        if (options.debug) {
          // eslint-disable-next-line no-console
          devLog(`🔧 Processing hunk ${hunkCount} at line ${oldStartLine} (adjusted to ${adjustedStartLine} with offset ${cumulativeOffset})`);
        }
        
        i++; // Move past @@ line
        
        // Collect hunk lines
        const hunkLines = [];
        while (i < diffLines.length) {
          const hunkLine = diffLines[i];
          
          // Stop at next hunk or end
          if (hunkLine.match(unifiedDiffRegex) || 
              hunkLine.startsWith('---') || 
              hunkLine.startsWith('+++')) {
            break;
          }
          
          if (hunkLine.startsWith('\\')) {
            // "\ No newline at end of file" marker - skip
            i++;
            continue;
          }
          
          hunkLines.push(hunkLine);
          i++;
        }
        
        // Apply hunk to result with options using adjusted position
        const hunkResult = applyHunk(resultLines, hunkLines, adjustedStartLine, oldLineCount, options);
        
        // Calculate offset from this hunk (lines added - lines removed)
        const linesAdded = hunkResult.changes.filter(c => c.type === 'insert').length;
        const linesRemoved = hunkResult.changes.filter(c => c.type === 'delete').length;
        const hunkOffset = linesAdded - linesRemoved;
        cumulativeOffset += hunkOffset;
        
        if (options.debug) {
          // eslint-disable-next-line no-console
          devLog(`   Hunk ${hunkCount}: +${linesAdded} -${linesRemoved} lines, offset: ${hunkOffset}, cumulative: ${cumulativeOffset}`);
        }
        
        resultLines = hunkResult.lines;
        changes.push(...hunkResult.changes);
        
      } else {
        i++;
      }
    }
    
    // eslint-disable-next-line no-console
    devLog(`✅ Successfully applied ${hunkCount} hunk(s) with ${changes.length} change(s)`);
    
  } catch (error) {
    // Context mismatch error - diff doesn't match current file
    // eslint-disable-next-line no-console
    console.error(`❌ Failed to apply diff: ${error.message}`);
    return {
      success: false,
      newContent: currentContent,
      changes: [],
      error: error.message
    };
  }
  
  return {
    success: true,
    newContent: resultLines.join('\n'),
    changes
  };
}

/**
 * Apply a single diff hunk to lines with fuzzy matching
 * 
 * CRITICAL ALGORITHM: Tracks position in result array as deletions/insertions occur
 * Key insight: deletions shrink the array, so we maintain `resultPosition` which tracks
 * our actual position in the ever-changing result array:
 * - Context lines (' '): advance resultPosition by 1
 * - Deletions ('-'): remove at resultPosition, DON'T advance (next line slides into place)
 * - Insertions ('+'): insert at resultPosition, advance by 1
 * 
 * Enhanced with:
 * - Fuzzy matching for line comparison
 * - Adaptive positioning with confidence scoring
 * - Pre-validation to prevent out-of-bounds errors
 * - Detailed logging for debugging
 * 
 * @param {array} lines - Current file lines
 * @param {array} hunkLines - Diff hunk lines
 * @param {number} oldStartLine - Starting line in old file (1-indexed)
 * @param {number} oldLineCount - Number of lines in old file
 * @param {object} options - Options for fuzzy matching
 * @returns {object} - {lines: array, changes: array, adjustedPosition: number}
 */
function applyHunk(lines, hunkLines, oldStartLine, oldLineCount, options = {}) {
  const {
    useFuzzyMatching = true,
    similarityThreshold = DEFAULT_SIMILARITY_THRESHOLD,
    maxMismatches = DEFAULT_MAX_MISMATCHES,
    searchWindow = DEFAULT_HUNK_SEARCH_WINDOW,
    debug = false
  } = options;
  
  const changes = [];
  const result = [...lines];
  
  // Detect file creation: starting at line 0 or empty file
  const isFileCreation = oldStartLine === 0 || (oldStartLine === 1 && lines.length === 0);
  
  // Find best position for this hunk using all lines (context + deletions)
  let actualStartLine;
  let positioningConfidence = 1.0; // Default to high confidence
  let exactMatches = 0;
  
  if (isFileCreation) {
    // For new files, always start at the beginning
    actualStartLine = 0;
    positioningConfidence = 1.0;
    if (debug) {
      // eslint-disable-next-line no-console
      devLog(`📄 File creation detected - starting at line 0`);
    }
  } else if (useFuzzyMatching && hunkLines.length > 0) {
    const positioning = findBestHunkPosition(result, hunkLines, oldStartLine, searchWindow);
    actualStartLine = positioning.position;
    positioningConfidence = positioning.confidence;
    exactMatches = positioning.exactMatches;
    
    // Log if we adjusted the position
    if (debug && actualStartLine !== oldStartLine - 1) {
      // eslint-disable-next-line no-console
      devLog(`📍 Adjusted hunk position from line ${oldStartLine} to ${actualStartLine + 1} (confidence: ${(positioningConfidence * 100).toFixed(1)}%, exact matches: ${exactMatches})`);
    }
    
    // Warn if confidence is low
    if (positioningConfidence < LOW_CONFIDENCE_WARNING) {
      // eslint-disable-next-line no-console
      console.warn(`⚠️  Low confidence match (${(positioningConfidence * 100).toFixed(1)}%) with ${exactMatches} exact matches - diff may not apply correctly`);
    }
  } else {
    actualStartLine = oldStartLine - 1;
  }
  
  // Validate that we have a reasonable match before proceeding (skip for file creation)
  // Use constant threshold to allow fuzzy matching with at least 1 exact match
  if (!isFileCreation && useFuzzyMatching && positioningConfidence < MIN_CONFIDENCE_THRESHOLD) {
    // eslint-disable-next-line no-console
    console.error(`❌ Position confidence too low (${(positioningConfidence * 100).toFixed(1)}%) - cannot safely apply hunk`);
    throw new Error(`Cannot find suitable position for hunk (confidence: ${(positioningConfidence * 100).toFixed(1)}%, exact matches: ${exactMatches})`);
  }
  
  // Additional validation: if confidence is low and we found a different position than suggested, reject it
  // This prevents applying diffs at wrong locations when content doesn't match well
  if (!isFileCreation && useFuzzyMatching && positioningConfidence < MIN_REPOSITION_CONFIDENCE && actualStartLine !== (oldStartLine - 1)) {
    // eslint-disable-next-line no-console
    console.error(`❌ Fuzzy match repositioned hunk with low confidence (${(positioningConfidence * 100).toFixed(1)}%)`);
    // eslint-disable-next-line no-console
    console.error(`   Original position: line ${oldStartLine}, matched position: line ${actualStartLine + 1}`);
    // eslint-disable-next-line no-console
    console.error(`   This likely means the diff is for a different version of the file`);
    throw new Error(`Hunk repositioning rejected: low confidence ${(positioningConfidence * 100).toFixed(1)}% match at different position (line ${actualStartLine + 1} vs expected ${oldStartLine}). File content mismatch.`);
  }
  
  // Pre-validate: Check if we can start at the actualStartLine
  // We don't validate the end position here because deletions will shrink the array as we go
  if (!isFileCreation && actualStartLine > result.length) {
    // eslint-disable-next-line no-console
    console.error(`❌ Hunk validation failed: Cannot start hunk at line ${actualStartLine + 1} - file only has ${result.length} lines`);
    throw new Error(`Hunk start position exceeds file bounds: line ${actualStartLine + 1} but file only has ${result.length} lines.`);
  }
  
  let currentLine = actualStartLine; // Use adjusted position
  let linesAdded = 0;
  let linesRemoved = 0;
  let strictMismatches = 0;
  
  // Track our actual position in the result array as we process the hunk
  let resultPosition = actualStartLine;
  
  for (const hunkLine of hunkLines) {
    const prefix = hunkLine[0];
    const content = hunkLine.substring(1);
    
    if (prefix === ' ') {
      // Context line - verify it matches with fuzzy matching
      // resultPosition already points to the correct line in result array
      const actualContent = result[resultPosition] || '';
      
      if (actualContent !== content) {
        const similarity = calculateSimilarity(content, actualContent);
        
        if (similarity < similarityThreshold) {
          strictMismatches++;
          // eslint-disable-next-line no-console
          console.warn(`⚠️  Context mismatch at line ${currentLine + 1} (similarity: ${(similarity * 100).toFixed(1)}%)`);
          // eslint-disable-next-line no-console
          console.warn(`  Expected: "${content}"`);
          // eslint-disable-next-line no-console
          console.warn(`  Found: "${actualContent}"`);
          
          if (strictMismatches >= maxMismatches) {
            // eslint-disable-next-line no-console
            console.error(`❌ Too many strict mismatches (${strictMismatches}). The diff may be based on a different version of the file.`);
            throw new Error(`Context mismatch: diff appears to be for a different version of the file (${strictMismatches} mismatches)`);
          }
        } else if (similarity < 1.0) {
          if (debug) {
            // eslint-disable-next-line no-console
            devLog(`✓ Fuzzy matched context at line ${currentLine + 1} (similarity: ${(similarity * 100).toFixed(1)}%)`);
          }
        }
      }
      currentLine++;
      resultPosition++; // Move forward in result array
      
    } else if (prefix === '-') {
      // Deletion - remove line from result array
      // resultPosition points to where we need to delete
      
      // Safety check - ensure we're not out of bounds
      if (resultPosition >= result.length) {
        // eslint-disable-next-line no-console
        console.error(`❌ Cannot delete line at index ${resultPosition} - file only has ${result.length} lines`);
        // eslint-disable-next-line no-console
        console.error(`   Original line: ${currentLine + 1}, result position: ${resultPosition}, lines added: ${linesAdded}, lines removed: ${linesRemoved}`);
        // eslint-disable-next-line no-console
        console.error(`   Expected to delete: "${content}"`);
        throw new Error(`Deletion index out of bounds: trying to delete line ${resultPosition + 1} but file only has ${result.length} lines. This diff appears to be for a different version of the file.`);
      }
      
      // Additional check - verify we're not trying to delete beyond reasonable bounds
      if (resultPosition < 0) {
        // eslint-disable-next-line no-console
        console.error(`❌ Negative deletion index ${resultPosition} detected`);
        throw new Error(`Invalid deletion index: ${resultPosition}. Diff computation error.`);
      }
      
      // Verify line to delete with fuzzy matching
      const actualContent = result[resultPosition];
      if (useFuzzyMatching) {
        const similarity = calculateSimilarity(content, actualContent);
        
        if (similarity < similarityThreshold) {
          strictMismatches++;
          // eslint-disable-next-line no-console
          console.warn(`⚠️  Deletion target mismatch at line ${resultPosition + 1} (similarity: ${(similarity * 100).toFixed(1)}%)`);
          // eslint-disable-next-line no-console
          console.warn(`  Expected to delete: "${content}"`);
          // eslint-disable-next-line no-console
          console.warn(`  Found: "${actualContent}"`);
          
          if (strictMismatches >= maxMismatches) {
            // eslint-disable-next-line no-console
            console.error(`❌ Too many deletion mismatches (${strictMismatches}). Cannot safely apply diff.`);
            throw new Error(`Deletion mismatch: cannot safely delete lines (${strictMismatches} mismatches)`);
          }
        } else if (similarity < 1.0) {
          if (debug) {
            // eslint-disable-next-line no-console
            devLog(`✓ Fuzzy matched deletion at line ${resultPosition + 1} (similarity: ${(similarity * 100).toFixed(1)}%): "${actualContent}"`);
          }
        }
      } else {
        // Strict mode - must match exactly
        if (actualContent !== content) {
          // eslint-disable-next-line no-console
          console.error(`❌ Exact deletion mismatch at line ${resultPosition + 1}`);
          // eslint-disable-next-line no-console
          console.error(`  Expected: "${content}"`);
          // eslint-disable-next-line no-console
          console.error(`  Found: "${actualContent}"`);
          throw new Error(`Deletion mismatch at line ${resultPosition + 1}`);
        }
      }
      
      if (debug) {
        // eslint-disable-next-line no-console
        devLog(`🗑️  Deleting line ${resultPosition + 1}: "${actualContent}"`);
      }
      result.splice(resultPosition, 1);
      linesRemoved++;
      
      changes.push({
        type: 'delete',
        lineNumber: currentLine + 1,
        content: content,
        actualContent: actualContent
      });
      
      currentLine++;
      // Note: resultPosition stays the same because we deleted a line - the next line moved into this position
      
    } else if (prefix === '+') {
      // Insertion - add new line to result array
      // Insert at current resultPosition
      
      if (debug) {
        // eslint-disable-next-line no-console
        devLog(`➕ Inserting at line ${resultPosition + 1}: "${content}"`);
      }
      
      result.splice(resultPosition, 0, content);
      linesAdded++;
      resultPosition++; // Move forward past the inserted line
      
      changes.push({
        type: 'insert',
        lineNumber: resultPosition, // Use resultPosition for accurate line number
        content: content
      });
    }
  }
  
  return {lines: result, changes, adjustedPosition: actualStartLine};
}

// ============================================================================
// MULTI-FILE DIFF SUPPORT
// ============================================================================

/**
 * Parse multi-file unified diff
 * Splits diff content into separate file diffs and parses each one
 * Automatically strips inline UI structured content blocks
 * @param {string} diffContent - Raw unified diff content (possibly multiple files)
 * @returns {array} - Array of parsed diffs, one per file
 */
export function parseMultiFileDiff(diffContent) {
  // Strip structured blocks first
  const cleanDiffContent = stripStructuredBlocks(diffContent);
  
  const fileDiffs = [];
  const lines = cleanDiffContent.split('\n');
  let currentFileStart = -1;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Detect start of a new file diff
    if (line.startsWith('--- ')) {
      // If we have a previous file diff, extract and parse it
      if (currentFileStart >= 0) {
        const fileDiffContent = lines.slice(currentFileStart, i).join('\n');
        const parsed = parseUnifiedDiff(fileDiffContent);
        if (parsed.isValid) {
          fileDiffs.push(parsed);
        }
      }
      
      // Start new file diff
      currentFileStart = i;
    }
  }
  
  // Don't forget the last file
  if (currentFileStart >= 0) {
    const fileDiffContent = lines.slice(currentFileStart).join('\n');
    const parsed = parseUnifiedDiff(fileDiffContent);
    if (parsed.isValid) {
      fileDiffs.push(parsed);
    }
  }
  
  return fileDiffs;
}

/**
 * Apply multi-file diff to a collection of files
 * Processes multiple file diffs and returns aggregate results
 * Automatically strips inline UI structured content blocks
 * @param {string} diffContent - Raw unified diff (possibly multiple files)
 * @param {object} fileContents - Map of filePath -> content
 * @param {object} options - Options for fuzzy matching
 * @returns {object} - {success: boolean, results: array, errors: array}
 */
export function applyMultiFileDiff(diffContent, fileContents, options = {}) {
  // Strip structured blocks first
  const cleanDiffContent = stripStructuredBlocks(diffContent);
  const fileDiffs = parseMultiFileDiff(cleanDiffContent);
  const results = [];
  const errors = [];
  
  if (fileDiffs.length === 0) {
    // Try as single file diff
    const singleParsed = parseUnifiedDiff(cleanDiffContent);
    if (singleParsed.isValid) {
      fileDiffs.push(singleParsed);
    }
  }
  
  // eslint-disable-next-line no-console
  devLog(`📦 Processing ${fileDiffs.length} file(s) from diff`);
  
  for (const parsed of fileDiffs) {
    const targetPath = parsed.targetPath;
    const currentContent = fileContents[targetPath] || '';
    
    try {
      const result = applyUnifiedDiff(parsed.diffLines.join('\n'), currentContent, options);
      
      results.push({
        path: targetPath,
        operation: result.operation,
        success: result.success,
        newContent: result.newContent,
        changes: result.changes
      });
      
      if (!result.success) {
        errors.push({
          path: targetPath,
          error: result.error
        });
      }
    } catch (error) {
      errors.push({
        path: targetPath,
        error: error.message
      });
      
      results.push({
        path: targetPath,
        success: false,
        error: error.message
      });
    }
  }
  
  const successCount = results.filter(r => r.success).length;
  // eslint-disable-next-line no-console
  devLog(`✅ Successfully applied ${successCount}/${fileDiffs.length} file diffs`);
  
  return {
    success: errors.length === 0,
    results,
    errors,
    totalFiles: fileDiffs.length,
    successfulFiles: successCount
  };
}

// ============================================================================
// UTILITY HELPERS
// ============================================================================

/**
 * Check if a string contains unified diff format
 * Quick validation without full parsing
 * @param {string} content - Content to check
 * @returns {boolean}
 */
export function isUnifiedDiff(content) {
  return /^---\s+.*\n\+\+\+\s+.*\n@@/m.test(content);
}

/**
 * Extract target file path from unified diff
 * Useful for routing diffs to correct files
 * @param {string} diffContent - Raw unified diff
 * @returns {string|null} - Target file path or null
 */
export function extractTargetPath(diffContent) {
  const parsed = parseUnifiedDiff(diffContent);
  return parsed.isValid ? parsed.targetPath : null;
}


